/**
 * WHICH ADDRESSES ARE A SHOP'S OWN PAGES (DK parity, S1d).
 *
 * The pages a shopper browses — the shop, a category, a brand, the collections,
 * a policy page, a product — and not the ones that hold the bottom of the
 * screen for a purchase in progress (checkout, order confirmation, payments),
 * nor the booking, stays and courses pages. On stylon.online these sit under the
 * shop's slug (App.js's platform tree); on a business's own domain, at the root.
 */
const SHOP_PAGES = [
  /^shop$/,
  /^shop\/c\/[^/]+$/,
  /^shop\/brands\/[^/]+$/,
  /^shop\/collections(?:\/[^/]+)?$/,
  /^shop\/policies\/[^/]+$/,
  /^p\/[^/]+$/,
];

const segmentsOf = (pathname) => String(pathname || '').split('/').filter(Boolean);

export function isShopSurface(pathname, domainMode) {
  const parts = segmentsOf(pathname);
  if (!domainMode && parts.length < 2) return false;
  const rest = (domainMode ? parts : parts.slice(1)).join('/');
  return SHOP_PAGES.some((page) => page.test(rest));
}

/** The first segment of the address, decoded: on stylon.online, the shop's slug. */
export function shopSlugOfPath(pathname) {
  const first = segmentsOf(pathname)[0] || '';
  try {
    return decodeURIComponent(first);
  } catch (_) {
    return first;
  }
}

/**
 * PAGES THAT OWN THE BOTTOM OF THE SCREEN, and so never carry the floating
 * button: a purchase in progress (checkout, the placed-order page, an order's
 * own page) and the booking confirmation. Everything else a customer of this
 * business can be looking at — the main page, the shop, a category, a brand, a
 * product, the gallery, courses, stays — may carry it.
 *
 * Expressed as what it must NOT cover rather than a list of what it may, which
 * is how the dock came to be invisible on the one page most customers land on.
 * `isShopSurface` above stays exactly as it was: it answers a different
 * question (is this the SHOP), and the cart bar and footer still read it.
 */
const BOTTOM_OWNED = [
  /^checkout$/,
  /^order-placed$/,
  /^orders(?:\/|$)/,
  /^booking-successful$/,
  /^add-appointment$/,
  /^booking$/,
  /^login$/,
  /^customer-onboard$/,
];

/**
 * Any page of THIS business a customer browses, where a floating button is
 * welcome. Same address rules as `isShopSurface`: on a business's own domain
 * the pages sit at the root; on stylon.online they sit under its slug, and the
 * caller still checks the slug is this shop's.
 */
export function isCustomerSurface(pathname, domainMode) {
  const parts = segmentsOf(pathname);
  if (!domainMode && parts.length < 1) return false;
  const rest = (domainMode ? parts : parts.slice(1)).join('/');
  // The business's own main page: nothing after the slug (or the bare root on
  // a custom domain). This is the page the floating button was asked for.
  if (!rest) return true;
  return !BOTTOM_OWNED.some((page) => page.test(rest));
}
